import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { api } from '../../services/api';

type SmsRead = {
  read_ok?: boolean;
  sms_enabled?: boolean | null;
  recipient_masked?: string | null;
};

type SwitchState = 'unknown' | 'off' | 'on';

export function BoothManualSmsControl({
  deviceId,
  cameraId,
  zoneId,
}: {
  deviceId: string | null;
  cameraId: string | null;
  zoneId: string | null;
  presenceFresh?: boolean;
}) {
  const [switchState, setSwitchState] = useState<SwitchState>('unknown');
  const [recipient, setRecipient] = useState<string | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const scopeReady = Boolean(deviceId && cameraId && zoneId);

  useEffect(() => {
    if (!scopeReady) {
      setSwitchState('unknown');
      setRecipient(null);
      return undefined;
    }
    let cancelled = false;
    const params = new URLSearchParams({
      device_id: deviceId || '',
      camera_id: cameraId || '',
      zone_id: zoneId || '',
    });
    api.get(`/api/v1/showcase/live-booth/sms?${params.toString()}`)
      .then((response) => {
        if (cancelled) return;
        applyRead((response?.data || {}) as SmsRead);
      })
      .catch(() => {
        if (cancelled) return;
        setSwitchState('unknown');
        setRecipient(null);
        setDetail('Unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [cameraId, deviceId, scopeReady, zoneId]);

  const applyRead = (body: SmsRead) => {
    if (body.read_ok !== true || typeof body.sms_enabled !== 'boolean') {
      setSwitchState('unknown');
      setRecipient(null);
      setDetail('Unavailable');
      return;
    }
    setSwitchState(body.sms_enabled ? 'on' : 'off');
    setRecipient(body.recipient_masked || null);
    setDetail(null);
  };

  const setEnabled = async (enabled: boolean) => {
    if (!scopeReady || writing || switchState === 'unknown') return;
    if ((enabled && switchState === 'on') || (!enabled && switchState === 'off')) return;
    setWriting(true);
    setDetail(null);
    try {
      const response = await api.post('/api/v1/showcase/live-booth/sms', {
        device_id: deviceId,
        camera_id: cameraId,
        zone_id: zoneId,
        enabled,
      });
      const body = (response?.data || {}) as SmsRead;
      if (body.read_ok !== true || body.sms_enabled !== enabled) {
        setSwitchState('unknown');
        setDetail('Unavailable');
        return;
      }
      applyRead(body);
    } catch {
      setSwitchState('unknown');
      setDetail('Unavailable');
    } finally {
      setWriting(false);
    }
  };

  const confirmedOff = switchState === 'off';
  const confirmedOn = switchState === 'on';
  const tooltip = recipient ? `Recipient ${recipient}` : undefined;

  return (
    <Box
      data-testid="booth-manual-sms"
      data-sms-enabled={switchState}
      title={tooltip}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75, width: '100%' }}
    >
      <Typography data-testid="booth-manual-sms-title" sx={{ fontWeight: 700, fontSize: 14, color: '#d5deea' }}>
        Send SMS
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} role="group" aria-label="Send SMS">
        <Box
          component="button"
          type="button"
          data-testid="booth-manual-sms-off"
          aria-pressed={confirmedOff}
          disabled={!scopeReady || writing || switchState === 'unknown'}
          onClick={() => { void setEnabled(false); }}
          sx={toggleSx(confirmedOff, '#7f1d1d', '#fecaca')}
        >
          OFF
        </Box>
        <Box
          component="button"
          type="button"
          data-testid="booth-manual-sms-on"
          aria-pressed={confirmedOn}
          disabled={!scopeReady || writing || switchState === 'unknown'}
          onClick={() => { void setEnabled(true); }}
          sx={toggleSx(confirmedOn, '#14532d', '#bbf7d0')}
        >
          ON
        </Box>
      </Box>
      <Typography data-testid="booth-manual-sms-status" sx={{ minWidth: detail ? 88 : 0, fontSize: 13, color: '#fbbf24' }}>
        {detail || ''}
      </Typography>
    </Box>
  );
}

function toggleSx(selected: boolean, fill: string, color: string) {
  return {
    minWidth: 52,
    border: selected ? `2px solid ${color}` : '1px solid rgba(255,255,255,0.24)',
    bgcolor: selected ? fill : 'transparent',
    color: selected ? color : '#6d7c90',
    borderRadius: 1,
    px: 1,
    py: 0.4,
    fontWeight: 800,
    letterSpacing: 0.4,
    cursor: 'pointer',
    fontSize: 13,
  };
}
